import { useTranslation } from 'react-i18next';
import { Textarea } from '@/components/ui/Textarea';
import { cn } from '@/utils/cn';
import { StepProps } from '../types';

/**
 * ImageStep - Step 4 of AI Briefing Wizard (Conditional)
 *
 * Allows user to request image suggestions and provide description.
 * Only shown when contentFormat is 'image_text' or 'carousel_text'.
 * Extracted from AIBriefingModal for wizard flow.
 */

export const ImageStep = ({ formData, onFieldChange, errors }: StepProps) => {
  const { t } = useTranslation('ai');

  return (
    <div className="space-y-5">
      {/* Image Suggestion Toggle */}
      <div className="flex items-center justify-between gap-3 bg-primary-50 border border-primary-100 rounded-lg px-4 py-3">
        <label className="text-sm font-medium text-gray-700">
          {t('ai_briefing_image_question')}
        </label>
        <div className="flex gap-1.5 flex-shrink-0">
          <button
            type="button"
            onClick={() => onFieldChange('wantImageSuggestion', true)}
            className={cn(
              'px-3 py-1 rounded-md text-xs font-medium transition-all',
              formData.wantImageSuggestion
                ? 'bg-primary-500 text-primary-contrast shadow-sm'
                : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'
            )}
            aria-pressed={formData.wantImageSuggestion}
          >
            {t('ai_briefing_image_yes')}
          </button>
          <button
            type="button"
            onClick={() => onFieldChange('wantImageSuggestion', false)}
            className={cn(
              'px-3 py-1 rounded-md text-xs font-medium transition-all',
              !formData.wantImageSuggestion
                ? 'bg-primary-500 text-primary-contrast shadow-sm'
                : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'
            )}
            aria-pressed={!formData.wantImageSuggestion}
          >
            {t('ai_briefing_image_no')}
          </button>
        </div>
      </div>

      {/* Image Description - only if wantImageSuggestion is true */}
      {formData.wantImageSuggestion && (
        <div>
          <Textarea
            label={t('ai_briefing_image_description_label')}
            placeholder={t('ai_briefing_image_description_placeholder')}
            value={formData.imageDescription}
            onChange={(e) => onFieldChange('imageDescription', e.target.value)}
            rows={4}
            autoResize
            error={errors?.imageDescription}
          />
          <p className="mt-2 text-sm text-gray-500">
            {t('ai_briefing_image_description_help')}
          </p>
        </div>
      )}
    </div>
  );
};
